import {
  useGetApiV1IndividualpaymentCourseFinancialsCoursefinancialidAvailablePaymentMethods,
  useGetApiV1IndividualpaymentCourseFinancialsCoursefinancialidInfo,
  useGetApiV1IndividualpaymentCourseRegistrationsCourseregistrationidCourseFinancials,
  useGetApiV1IndividualpaymentSharedCourseRegistrations,
} from '@/shared/api/generated';
import { type LookupOption, mapDropdownOptions } from '@/shared/ui/molecules/LookupAutocomplete';

/**
 * Matrículas de uma pessoa, para o segundo campo do bloco de vínculo.
 *
 * A query fica desabilitada enquanto não houver `personId`: sem ele o endpoint
 * devolveria as matrículas de todo mundo, e o campo ficaria populado com opções
 * que não pertencem à pessoa escolhida.
 *
 * @param personId - Id numérico da pessoa; `null` desabilita a query.
 * @returns `{ courseRegistrations, isLoading }`
 *
 * @example
 * ```tsx
 * const { courseRegistrations } = usePersonCourseRegistrations(260953);
 * ```
 */
export function usePersonCourseRegistrations(personId: number | null): {
  readonly courseRegistrations: readonly LookupOption[];
  readonly isLoading: boolean;
} {
  const enabled = personId !== null;

  const { data, isLoading } = useGetApiV1IndividualpaymentSharedCourseRegistrations(
    enabled ? { personId } : undefined,
    { query: { enabled } },
  );

  return {
    courseRegistrations: mapDropdownOptions(data),
    isLoading: enabled && isLoading,
  } as const;
}

/**
 * Planos financeiros do curso de uma matrícula — o terceiro campo do vínculo.
 *
 * @param courseRegistrationId - Matrícula escolhida; `null` desabilita a query.
 * @returns `{ courseFinancials, isLoading }`
 *
 * @example
 * ```tsx
 * const { courseFinancials } = useCourseRegistrationFinancials(4321);
 * ```
 */
export function useCourseRegistrationFinancials(courseRegistrationId: number | null): {
  readonly courseFinancials: readonly LookupOption[];
  readonly isLoading: boolean;
} {
  const enabled = courseRegistrationId !== null;

  const { data, isLoading } =
    useGetApiV1IndividualpaymentCourseRegistrationsCourseregistrationidCourseFinancials(
      courseRegistrationId ?? 0,
      undefined,
      { query: { enabled } },
    );

  return { courseFinancials: mapDropdownOptions(data), isLoading: enabled && isLoading } as const;
}

/** Valores sugeridos pelo plano financeiro do curso. */
export interface CourseFinancialInfo {
  readonly totalValue: number | null;
  readonly valueRegistration: number | null;
  readonly discountPunctualPercentage: number | null;
}

/**
 * Valores que o plano financeiro do curso sugere para o plano em criação.
 *
 * São **sugestão**, não imposição: a tela pré-preenche os campos e o operador
 * pode sobrescrevê-los antes de salvar.
 *
 * @param courseFinancialId - Plano financeiro escolhido; `null` desabilita a query.
 * @returns `{ info, isLoading }` — `info` é `null` enquanto não há resposta.
 *
 * @example
 * ```tsx
 * const { info } = useCourseFinancialInfo(77);
 * // info?.totalValue → valor cheio do curso
 * ```
 */
export function useCourseFinancialInfo(courseFinancialId: number | null): {
  readonly info: CourseFinancialInfo | null;
  readonly isLoading: boolean;
} {
  const enabled = courseFinancialId !== null;

  const { data, isLoading } = useGetApiV1IndividualpaymentCourseFinancialsCoursefinancialidInfo(
    courseFinancialId ?? 0,
    { query: { enabled } },
  );

  const info: CourseFinancialInfo | null =
    data === undefined
      ? null
      : {
          totalValue: data.totalValue ?? null,
          valueRegistration: data.valueRegistration ?? null,
          discountPunctualPercentage: data.discountPunctualPercentage ?? null,
        };

  return { info, isLoading: enabled && isLoading } as const;
}

/**
 * Formas de pagamento aceitas por um plano financeiro do curso.
 *
 * A edição consulta o equivalente por contrato
 * (`financial-people/{financialPersonId}/available-payment-methods`), que não
 * serve aqui: o contrato ainda não existe. Este endpoint responde a mesma
 * pergunta a partir do plano do curso.
 *
 * @param courseFinancialId - Plano financeiro escolhido; `null` desabilita a query.
 * @returns `{ paymentMethods, isLoading }`
 *
 * @example
 * ```tsx
 * const { paymentMethods } = useCourseFinancialPaymentMethods(77);
 * ```
 */
export function useCourseFinancialPaymentMethods(courseFinancialId: number | null): {
  readonly paymentMethods: readonly LookupOption[];
  readonly isLoading: boolean;
} {
  const enabled = courseFinancialId !== null;

  const { data, isLoading } =
    useGetApiV1IndividualpaymentCourseFinancialsCoursefinancialidAvailablePaymentMethods(
      courseFinancialId ?? 0,
      undefined,
      { query: { enabled } },
    );

  return { paymentMethods: mapDropdownOptions(data), isLoading: enabled && isLoading } as const;
}
